import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './PublicPage.css';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Generate random number between min and max (inclusive)
const randomNum = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

// SVG Icons
const EmailIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <path d="M22 7l-10 6L2 7"/>
    </svg>
);

const LocationIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
        <circle cx="12" cy="10" r="3"/>
    </svg>
);

const CompanyIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/>
        <path d="M5 21V7l8-4v18"/>
        <path d="M19 21V11l-6-4"/>
        <path d="M9 9v.01"/>
        <path d="M9 12v.01"/>
        <path d="M9 15v.01"/>
        <path d="M9 18v.01"/>
    </svg>
);

export default function ContactPage() {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [honeypot, setHoneypot] = useState(''); // Hidden field for bots
    const [captchaNum1, setCaptchaNum1] = useState(0);
    const [captchaNum2, setCaptchaNum2] = useState(0);
    const [captchaAnswer, setCaptchaAnswer] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    // Generate new captcha numbers
    const generateCaptcha = useCallback(() => {
        setCaptchaNum1(randomNum(1, 15));
        setCaptchaNum2(randomNum(1, 10));
        setCaptchaAnswer('');
    }, []);

    // Generate captcha on mount and after successful submission
    useEffect(() => {
        generateCaptcha();
    }, [generateCaptcha]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const answer = parseInt(captchaAnswer, 10);
        if (isNaN(answer)) {
            setErrorMessage(t('contact.captcha_required'));
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const response = await fetch(`${API_BASE}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    name, 
                    email, 
                    message, 
                    captchaNum1,
                    captchaNum2,
                    captchaAnswer: answer,
                    honeypot 
                }),
            });

            if (response.ok) {
                setSubmitStatus('success');
                setName('');
                setEmail('');
                setMessage('');
                generateCaptcha();
            } else {
                const data = await response.json();
                setSubmitStatus('error');
                setErrorMessage(data.error || t('contact.error_generic'));
                generateCaptcha(); // Generate new captcha on error too
            }
        } catch {
            setSubmitStatus('error');
            setErrorMessage(t('contact.error_generic'));
            generateCaptcha();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="public-page contact-page">
            <header className="public-hero">
                <div className="public-hero-content">
                    <h1>{t('contact.hero_title')}</h1>
                    <p>{t('contact.hero_subtitle')}</p>
                </div>
            </header>

            <section className="public-section white-bg">
                <div className="section-inner">
                    <div className="contact-grid">
                        <div className="contact-info">
                            <h2>{t('contact.get_in_touch')}</h2>
                            <p>{t('contact.info_desc')}</p>

                            <div className="info-item">
                                <div className="icon">
                                    <EmailIcon />
                                </div>
                                <div className="text">
                                    <strong>{t('contact.email')}</strong>
                                    <p>hello@raisa.chat</p>
                                </div>
                            </div>
                            <div className="info-item">
                                <div className="icon">
                                    <LocationIcon />
                                </div>
                                <div className="text">
                                    <strong>{t('contact.location')}</strong>
                                    <p>Suceava, Romania</p>
                                </div>
                            </div>
                            <div className="info-item">
                                <div className="icon">
                                    <CompanyIcon />
                                </div>
                                <div className="text">
                                    <strong>{t('contact.company')}</strong>
                                    <p>RAISIS GROUP SRL<br />CUI: RO41337179</p>
                                </div>
                            </div>
                        </div>

                        <div className="modern-card contact-form-wrapper">
                            {submitStatus === 'success' ? (
                                <div className="success-message">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <path d="M9 12l2 2 4-4"/>
                                    </svg>
                                    <h3>{t('contact.success_title')}</h3>
                                    <p>{t('contact.success_message')}</p>
                                    <button 
                                        className="submit-btn-secondary"
                                        onClick={() => setSubmitStatus('idle')}
                                    >
                                        {t('contact.send_another')}
                                    </button>
                                </div>
                            ) : (
                                <form className="contact-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>{t('contact.name')}</label>
                                        <input 
                                            type="text" 
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder={t('contact.your_name')}
                                            required
                                            minLength={2}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('contact.email')}</label>
                                        <input 
                                            type="email" 
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder={t('contact.your_email')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('contact.message')}</label>
                                        <textarea 
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={t('contact.how_help')}
                                            required
                                            minLength={10}
                                            maxLength={2000}
                                        />
                                    </div>
                                    
                                    {/* Honeypot field - hidden from users, visible to bots */}
                                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                                        <input
                                            type="text"
                                            name="website"
                                            value={honeypot}
                                            onChange={(e) => setHoneypot(e.target.value)}
                                            tabIndex={-1}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div className="form-group captcha-group">
                                        <label>{t('contact.captcha_question')}</label>
                                        <div className="math-captcha">
                                            <span className="captcha-question">
                                                {captchaNum1} + {captchaNum2} = ?
                                            </span>
                                            <input
                                                type="number"
                                                value={captchaAnswer}
                                                onChange={(e) => setCaptchaAnswer(e.target.value)}
                                                placeholder={t('contact.captcha_placeholder')}
                                                required
                                                min="0"
                                                max="50"
                                                className="captcha-input"
                                            />
                                        </div>
                                    </div>

                                    {errorMessage && (
                                        <div className="error-message">
                                            {errorMessage}
                                        </div>
                                    )}

                                    <button 
                                        type="submit" 
                                        className="submit-btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t('contact.sending') : t('contact.send_message')}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
